import { useRef } from "react";
import { RotateCcw, FlipHorizontal2 } from "lucide-react";
import { coverSrc } from "../lib/images";

/**
 * Lets the admin pick which part of an image stays in view (CSS object-position)
 * and, for banners, how far to zoom in around that point.
 * value = { x, y, zoom? } as percentages (x,y 0..100) and a scale (zoom >= 1).
 * showZoom → renders the zoom slider (banners); omit for covers.
 * Renders the full image with a draggable marker + a wide and a mobile preview.
 */
export default function FocalPointPicker({ image, value, onChange, testid = "focal", showZoom = false, showVeil = false }) {
  const ref = useRef(null);
  const x = Number.isFinite(value?.x) ? value.x : 50;
  const y = Number.isFinite(value?.y) ? value.y : 50;
  const zoom = Number.isFinite(value?.zoom) ? value.zoom : 1;
  const veil = Number.isFinite(value?.veil) ? value.veil : 0;
  const flip = !!value?.flip;

  if (!image) return null;

  const emit = (patch) => onChange({ x, y, zoom, veil, flip, ...patch });

  const setFromEvent = (clientX, clientY) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    let nx = Math.round(((clientX - rect.left) / rect.width) * 100);
    let ny = Math.round(((clientY - rect.top) / rect.height) * 100);
    nx = Math.max(0, Math.min(100, nx));
    ny = Math.max(0, Math.min(100, ny));
    emit({ x: nx, y: ny });
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    setFromEvent(e.clientX, e.clientY);
    const move = (ev) => setFromEvent(ev.clientX, ev.clientY);
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };

  const previewStyle = {
    objectPosition: `${x}% ${y}%`,
    transform: `scale(${zoom}) scaleX(${flip ? -1 : 1})`,
    transformOrigin: `${x}% ${y}%`,
  };

  return (
    <div className="mt-3" data-testid={`${testid}-picker`}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint">
          Focal point — drag the dot to choose what stays in view
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => emit({ flip: !flip })}
            data-testid={`${testid}-flip`}
            aria-pressed={flip}
            className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${flip ? "border-crimson text-crimson bg-crimson/10" : "border-line text-vellum-muted hover:border-crimson hover:text-crimson"}`}
          >
            <FlipHorizontal2 className="w-3 h-3" /> Flip
          </button>
          <button
            type="button"
            onClick={() => emit({ x: 50, y: 50, zoom: 1, veil: 0, flip: false })}
            data-testid={`${testid}-reset`}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-line text-vellum-muted hover:border-crimson hover:text-crimson transition-colors"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        {/* draggable source image */}
        <div
          ref={ref}
          onPointerDown={onPointerDown}
          data-testid={`${testid}-canvas`}
          className="relative w-full border border-line cursor-crosshair select-none touch-none overflow-hidden"
        >
          <img src={coverSrc(image, "detail")} alt="" draggable={false} className="w-full block pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none bg-black/10" />
          <div
            className="absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full border-2 border-crimson ring-2 ring-white/70 shadow pointer-events-none"
            style={{ left: `${x}%`, top: `${y}%` }}
            data-testid={`${testid}-marker`}
          >
            <span className="absolute inset-1/2 w-1 h-1 -ml-0.5 -mt-0.5 rounded-full bg-crimson" />
          </div>
        </div>

        {/* live previews: wide (desktop) + tall (mobile) */}
        <div>
          <div className="flex gap-3 items-start">
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint mb-2">Wide / desktop</p>
              <div className="relative w-full aspect-[21/9] border border-line overflow-hidden bg-ink">
                <img src={coverSrc(image, "detail")} alt="" className="w-full h-full object-cover" style={previewStyle} data-testid={`${testid}-preview`} />
                {showVeil && veil > 0 && <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: veil / 100 }} />}
              </div>
            </div>
            <div className="shrink-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint mb-2">Mobile</p>
              <div className="relative w-[68px] aspect-[9/16] border border-line overflow-hidden bg-ink rounded-md">
                <img src={coverSrc(image, "detail")} alt="" className="w-full h-full object-cover" style={previewStyle} data-testid={`${testid}-preview-mobile`} />
                {showVeil && veil > 0 && <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: veil / 100 }} />}
              </div>
            </div>
          </div>

          <p className="mt-2 font-mono text-[10px] text-vellum-faint" data-testid={`${testid}-readout`}>
            x {x}% · y {y}%{showZoom ? ` · zoom ${zoom.toFixed(2)}×` : ""}{showVeil ? ` · veil ${veil}%` : ""}{flip ? " · flipped" : ""}
          </p>

          {showZoom && (
            <div className="mt-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint">Zoom</label>
              <input
                type="range"
                min="1"
                max="2.5"
                step="0.05"
                value={zoom}
                onChange={(e) => emit({ zoom: parseFloat(e.target.value) })}
                data-testid={`${testid}-zoom`}
                className="w-full accent-crimson mt-1"
              />
            </div>
          )}

          {showVeil && (
            <div className="mt-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-vellum-faint">Darkening veil — keeps title text readable ({veil}%)</label>
              <input
                type="range"
                min="0"
                max="80"
                step="1"
                value={veil}
                onChange={(e) => emit({ veil: parseInt(e.target.value, 10) })}
                data-testid={`${testid}-veil`}
                className="w-full accent-crimson mt-1"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
